import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { DateTime, getDateTime } from 'infras/datetime';
import { post } from 'infras/rest_client';
import { AssistantSummarizePostBody } from 'integrations/assistant/presentations/api/schema/assistant';
import { AssistantUserSettingItem } from 'integrations/assistant/presentations/api/schema/assistant_user_setting';
import { marked } from 'marked';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import { apiPath } from 'presentations/routes/path';
import { setEvent } from 'presentations/views/helpers/analytics';
import { useRequest } from 'presentations/views/hooks/request';
import { SyntheticEvent, useContext, useEffect, useState } from 'react';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';

interface Props {
  viewer: ViewerItem;
}

export const useSummarizeView = (props: Props) => {
  const t = translateInComponent([translations]);
  const portalContext = useContext(DashboardPortalContext);
  const [content, setContent] = useState<string | null>(null);
  const [messageStream, setMessageStream] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    { role: 'system' | 'user'; content: string }[]
  >([{ role: 'user', content: t('first message') }]);
  const [isLoaded, setIsLoaded] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSetting, setIsShowSetting] = useState<boolean>(false);
  const [startedAtFilter, setStartedAtFilter] = useState<DateTime | undefined>(
    undefined,
  );
  const [endedAtFilter, setEndedAtFilter] = useState<DateTime | undefined>(
    undefined,
  );

  useEffect(() => {
    const startedAt = getDateTime().startOf('day');
    setStartedAtFilter(startedAt);
    setEndedAtFilter(startedAt.plus({ day: 0 }));
    setIsLoaded(false);
  }, []);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem = userProfileRequest.data;

  const settingRequest = useRequest(
    apiPath(`/v1/integrations/assistant/user/settings`),
  );
  const setting: AssistantUserSettingItem | null = settingRequest.data ?? null;

  useEffect(() => {
    if (isLoaded) return;
    init();
  }, [isLoaded]);

  const createMessageStream = async (text: string) => {
    const strings = text.split('');
    let duration = 0;
    let stream = '';
    for (let i = 0; i < strings.length; i++) {
      const random = Math.floor(Math.random() * 4);
      duration += random * 20;
      setTimeout(() => {
        stream += strings[i];
        setMessageStream(marked(stream));
      }, duration);
    }
    setTimeout(() => {
      setMessages([...messages, { role: 'system', content: marked(text) }]);
      setMessageStream(null);
    }, duration + 1000);
  };

  const onChangeMessage = (e: SyntheticEvent<HTMLInputElement>) => {
    setContent(e.currentTarget.value);
  };

  const onSubmitMessage = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!content) return;
    setMessages([...messages, { role: 'user', content }]);
    setContent('');
    setIsLoaded(false);
    setEvent('submit chat assistant add message', {
      view: 'summary',
    });
  };

  const onClickSetting = () => {
    setIsShowSetting(true);
    setEvent('show chat assistant settings', {
      view: 'summary',
    });
  };

  const onCloseSetting = () => {
    setIsShowSetting(false);
  };

  const init = async () => {
    await onSubmit();
  };

  const onSubmit = async () => {
    if (isLoading) return;
    if (!startedAtFilter || !endedAtFilter) return;
    const data: AssistantSummarizePostBody = {
      messages,
      startedAt: startedAtFilter.toMillis(),
      endedAt: endedAtFilter.plus({ day: 1 }).toMillis(),
    };
    setIsLoading(true);
    const result = await post(apiPath(`/v1/integrations/assistant/summarize`), {
      data,
      timeout: 60 * 1 * 1000, // 1 min
    });
    if (!result.isSuccess) {
      setIsLoading(false);
      setIsLoaded(true);
      portalContext.dispatch(setErrorMessage(t('Update error')));
      return;
    }
    await createMessageStream(result.data.answer);
    setIsLoading(false);
    setIsLoaded(true);
  };

  const onChangeDateFilter = (startedAt?: Date, endedAt?: Date) => {
    if (startedAt) {
      setStartedAtFilter(getDateTime(startedAt, userProfile.timeZone));
    }
    if (endedAt) {
      setEndedAtFilter(getDateTime(endedAt, userProfile.timeZone));
    }
    setIsLoaded(false);
  };

  return {
    state: {
      content,
      isNoContent: !Boolean(content),
      messages,
      isLoading: !isLoaded || isLoading,
      isShowSetting,
      messageStream,
      startedAtFilter,
      endedAtFilter,
      userProfile,
      setting,
    },
    actions: {
      onChangeMessage,
      onSubmitMessage,
      onClickSetting,
      onCloseSetting,
      onChangeDateFilter,
    },
  };
};
